import { useEffect, useState } from 'react';
import { FileNames } from '../Constants/FileNames';
import FileEndpoints from '../Services/FileEndpoints';
import DataEndpoints from '../Services/DataEndpoints';
import { useCreateToast } from './useCreateToast';

export const CHART_MENU_TYPES = {
    INTERACTIVE: 'interactive',
    MARKET_SNAPSHOT: 'marketSnapshot',
};

const MARKET_SNAPSHOT_CATEGORY = 'Market Snapshot';
const MARKET_SNAPSHOT_TITLE = 'market snapshot';
let cachedChartCategories = null;
let chartCategoriesRequest = null;

export const resetChartCategoriesCache = () => {
    cachedChartCategories = null;
    chartCategoriesRequest = null;
};

export const isMarketSnapshotChart = (chart) =>
    (chart?.title || chart?.name || '').trim().toLowerCase() === MARKET_SNAPSHOT_TITLE;

export const filterChartCategoriesForMenu = (categories, menuType = CHART_MENU_TYPES.INTERACTIVE) => {
    const safeCategories = Array.isArray(categories) ? categories : [];

    return safeCategories.reduce((nextCategories, category) => {
        const charts = Array.isArray(category?.charts) ? category.charts : [];
        const filteredCharts = charts.filter((chart) => {
            const isMarketSnapshot = isMarketSnapshotChart(chart);
            return menuType === CHART_MENU_TYPES.MARKET_SNAPSHOT ? isMarketSnapshot : !isMarketSnapshot;
        });

        if (!filteredCharts.length) {
            return nextCategories;
        }

        nextCategories.push({
            ...category,
            charts: filteredCharts,
        });

        return nextCategories;
    }, []);
};

export const useChartCategories = ({ enabled = true } = {}) => {
    const [chartCategories, setChartCategories] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const { createErrorToast } = useCreateToast();

    const normalizeChartCategories = (categories) => {
        const safeCategories = Array.isArray(categories) ? categories : [];
        const nextCategories = [];
        const sizingAnalyticsCharts = [];

        safeCategories.forEach((category, index) => {
            const charts = Array.isArray(category?.charts) ? category.charts : [];
            const remainingCharts = [];

            charts.forEach((chart) => {
                if (isMarketSnapshotChart(chart)) {
                    sizingAnalyticsCharts.push(chart);
                    return;
                }

                remainingCharts.push(chart);
            });

            if (remainingCharts.length > 0) {
                nextCategories.push({
                    ...category,
                    order: category?.order ?? index,
                    charts: remainingCharts,
                });
            }
        });

        if (!sizingAnalyticsCharts.length) {
            return nextCategories;
        }

        const existingSizingAnalyticsCategory = nextCategories.find(
            (category) => category?.displayName === MARKET_SNAPSHOT_CATEGORY
        );

        if (existingSizingAnalyticsCategory) {
            existingSizingAnalyticsCategory.charts = [
                ...existingSizingAnalyticsCategory.charts,
                ...sizingAnalyticsCharts,
            ];
            return nextCategories;
        }

        return [
            ...nextCategories,
            {
                displayName: MARKET_SNAPSHOT_CATEGORY,
                order: nextCategories.length,
                charts: sizingAnalyticsCharts,
            },
        ];
    };

    const buildChartStoreCategories = (charts) => {
        const items = Array.isArray(charts) ? charts : [];
        const itemsById = new Map(items.map(item => [item.chartId, item]));

        const toChart = chart => ({
            name: chart.name,
            title: chart.title,
            order: chart.order,
            chartId: chart.chartId,
            routeId: chart.chartId,
            datasetId: chart.datasetId,
            configuration: chart.configuration,
            thumbnail: chart.thumbnail,
            thumbnailMimetype: chart.thumbnailMimeType,
            hasPreview: chart.hasPreview,
            previewMimetype: chart.previewMimeType,
        });

        const categoriesByParentId = new Map();
        items
            .filter(item => !item.isFolder)
            .forEach(chart => {
                const parentId = chart.parentChartId ?? '';
                if (!categoriesByParentId.has(parentId)) {
                    categoriesByParentId.set(parentId, []);
                }
                categoriesByParentId.get(parentId).push(chart);
            });

        return normalizeChartCategories(Array.from(categoriesByParentId.entries())
            .map(([parentId, groupedCharts]) => {
                const parentFolder = itemsById.get(parentId);
                return {
                    displayName: parentFolder?.title || parentFolder?.displayName || '',
                    order: parentFolder?.order ?? 0,
                    charts: groupedCharts
                        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
                        .map(toChart),
                };
            })
                    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)));
    };

    const fetchFoldersandFiles = async (fileTitle, fileName, parentId = null) => {
        try {
            const { data } = await FileEndpoints.getFiles(parentId, fileName, fileTitle);
            return data;
        } catch (error) {
            createErrorToast(error);
            return [];
        }
    };

    const processChartData = async () => {
        const rootLevelData = await fetchFoldersandFiles(
            null,
            FileNames.VISUALIZATIONS,
            null
        );

        const safeRootData = Array.isArray(rootLevelData) ? rootLevelData : [];
        const folders = safeRootData
            .filter(item => item?.isFolder)
            .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

        const updatedCategories = [];

        for (let folder of folders) {
            const folderLevelData = await fetchFoldersandFiles(
                null,
                null,
                folder.fileId
            );

            const safeFolderData = Array.isArray(folderLevelData) ? folderLevelData : [];
            const children = safeFolderData
                .filter(file => !file?.isFolder && file?.parentFileId === folder.fileId)
                .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

            updatedCategories.push({
                displayName: folder.title,
                order: folder.order,
                charts: children,
            });
        }

        return normalizeChartCategories(updatedCategories);
    };

    const processChartPermissionData = async () => {
        try {
            const { data } = await DataEndpoints.getChartPermission();
            if (data?.hasChartAccess) {
                return buildChartStoreCategories(data.charts);
            }
        } catch (error) {
            // If chart permission endpoint is unavailable, continue with file-based categories.
        }

        return null;
    };

    const loadChartCategories = async () => {
        const permissionCategories = await processChartPermissionData();
        if (permissionCategories) {
            return permissionCategories;
        }

        return processChartData();
    };

    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            setChartCategories([]);
            return;
        }

        let isMounted = true;
        setLoading(true);

        (async () => {
            try {
                if (cachedChartCategories) {
                    if (isMounted) {
                        setChartCategories(cachedChartCategories);
                    }
                    return;
                }

                if (!chartCategoriesRequest) {
                    chartCategoriesRequest = loadChartCategories()
                        .then((categories) => {
                            cachedChartCategories = categories;
                            return categories;
                        })
                        .finally(() => {
                            chartCategoriesRequest = null;
                        });
                }

                const categories = await chartCategoriesRequest;
                if (isMounted) {
                    setChartCategories(categories);
                }
            } catch (error) {
                if (isMounted) {
                    createErrorToast(error);
                    setChartCategories([]);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [enabled]);

    return { chartCategories, loading };
}
